import { Undo2Icon } from "lucide-react";

import { formatRelativeTimeLabel } from "../../timestampFormat";
import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { compactActivityCardTimeLabel } from "./activityThreadCard.logic";
import { resolveArchivedTimestamp } from "./activitySidebar.logic";
import type { ActivityArchivedRowProps } from "./ActivityArchivedRow";
import { SidebarThreadArchiveControl } from "./SidebarThreadArchiveControl";
import type { useSidebarThreadRow } from "./useSidebarThreadRow";

type ActivityArchivedRowState = ReturnType<typeof useSidebarThreadRow>;
type ArchiveWorkflowState =
  | NonNullable<ActivityArchivedRowProps["thread"]["archive"]>["state"]
  | null;

const ACTION_REVEAL_CLASS_NAME =
  "pointer-events-none inline-flex opacity-0 transition-opacity motion-reduce:transition-none group-hover/menu-sub-item:pointer-events-auto group-hover/menu-sub-item:opacity-100 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:opacity-100";

interface ArchivedRowActionModel {
  readonly archiveState: "hidden" | "available" | "confirming";
  readonly archiveLabel: "Archive" | "Retry";
  readonly hasActions: boolean;
  readonly restoreLabel: "Cancel archive" | "Restore" | "Unsettle";
  readonly showRestore: boolean;
  readonly statusLabel: "Applying" | "Archived" | "Failed" | "Requested" | null;
  readonly statusSlotWidthClass: string;
}

function resolveArchiveState(
  canArchive: boolean,
  isConfirmingArchive: boolean,
): ArchivedRowActionModel["archiveState"] {
  if (!canArchive) return "hidden";
  return isConfirmingArchive ? "confirming" : "available";
}

function resolveStatusSlotWidth(canArchive: boolean, canRestore: boolean): string {
  return canArchive && canRestore ? "min-w-[calc(var(--acard)*3)]" : "min-w-[calc(var(--acard)*2)]";
}

function resolveRestoreLabel(
  workflowState: ArchiveWorkflowState,
  isArchived: boolean,
): ArchivedRowActionModel["restoreLabel"] {
  if (workflowState === "requested" || workflowState === "failed") return "Cancel archive";
  return isArchived ? "Restore" : "Unsettle";
}

function resolveStatusLabel(
  workflowState: ArchiveWorkflowState,
): ArchivedRowActionModel["statusLabel"] {
  const labels = {
    requested: "Requested",
    applying: "Applying",
    failed: "Failed",
    archived: "Archived",
  } as const;
  return workflowState === null ? null : (labels[workflowState] ?? null);
}

function canArchiveThread(
  workflowState: ArchiveWorkflowState,
  isArchived: boolean,
  isThreadRunning: boolean,
): boolean {
  return (
    !isThreadRunning && (workflowState === "failed" || (workflowState === null && !isArchived))
  );
}

function resolveActionModel(
  rowProps: ActivityArchivedRowProps,
  state: ActivityArchivedRowState,
): ArchivedRowActionModel {
  const workflowState = rowProps.thread.archive?.state ?? null;
  const isArchived = workflowState === "archived" || rowProps.thread.archivedAt != null;
  const canRestore = workflowState !== "applying";
  const canArchive = canArchiveThread(workflowState, isArchived, state.isThreadRunning);
  const archiveState = resolveArchiveState(canArchive, state.isConfirmingArchive);
  const showRestore = !state.isConfirmingArchive && canRestore;
  const restoreLabel = resolveRestoreLabel(workflowState, isArchived);
  const statusLabel = resolveStatusLabel(workflowState);
  return {
    archiveState,
    archiveLabel: workflowState === "failed" ? "Retry" : "Archive",
    hasActions: archiveState !== "hidden" || showRestore,
    restoreLabel,
    showRestore,
    statusLabel,
    statusSlotWidthClass: resolveStatusSlotWidth(canArchive, canRestore),
  };
}

function ArchiveAction(props: {
  readonly rowProps: ActivityArchivedRowProps;
  readonly state: ActivityArchivedRowState;
  readonly archiveState: ArchivedRowActionModel["archiveState"];
  readonly archiveLabel: ArchivedRowActionModel["archiveLabel"];
}) {
  if (props.archiveState === "hidden") return null;
  const { rowProps, state } = props;
  if (props.archiveState === "confirming") {
    return (
      <span className={ACTION_REVEAL_CLASS_NAME}>
        <SidebarThreadArchiveControl
          state="confirming"
          placement="cluster"
          threadId={rowProps.thread.id}
          title={rowProps.thread.title}
          confirmLabel={`Confirm ${props.archiveLabel.toLowerCase()}`}
          confirmButtonRef={state.handleConfirmArchiveRef}
          onPointerDown={state.stopPropagationOnPointerDown}
          onConfirm={state.handleConfirmArchiveClick}
        />
      </span>
    );
  }
  return (
    <span className={ACTION_REVEAL_CLASS_NAME}>
      <SidebarThreadArchiveControl
        state="available"
        placement="cluster"
        threadId={rowProps.thread.id}
        title={rowProps.thread.title}
        actionLabel={props.archiveLabel}
        confirmBeforeArchive={rowProps.appSettingsConfirmThreadArchive}
        onPointerDown={state.stopPropagationOnPointerDown}
        onStartConfirmation={state.handleStartArchiveConfirmation}
        onArchiveImmediate={state.handleArchiveImmediateClick}
      />
    </span>
  );
}

function RestoreAction(props: {
  readonly rowProps: ActivityArchivedRowProps;
  readonly state: ActivityArchivedRowState;
  readonly model: ArchivedRowActionModel;
}) {
  if (!props.model.showRestore) return null;
  const { rowProps, state, model } = props;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-thread-selection-safe
            data-testid={`thread-restore-${rowProps.thread.id}`}
            aria-label={`${model.restoreLabel} ${rowProps.thread.title}`}
            className="pointer-events-none inline-flex cursor-pointer items-center rounded-md bg-transparent px-[calc(var(--acard)*0.375)] text-muted-foreground opacity-0 transition-opacity motion-reduce:transition-none hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring group-hover/menu-sub-item:pointer-events-auto group-hover/menu-sub-item:opacity-100"
            onPointerDown={state.stopPropagationOnPointerDown}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              rowProps.onRestore(state.threadRef);
            }}
          >
            <Undo2Icon aria-hidden className="mb-px size-[calc(var(--acard)*0.875)]" />
          </button>
        }
      />
      <TooltipPopup side="top">{model.restoreLabel}</TooltipPopup>
    </Tooltip>
  );
}

function ageLabel(thread: ActivityArchivedRowProps["thread"]): string {
  const timestamp = resolveArchivedTimestamp(thread);
  return timestamp === null ? "" : compactActivityCardTimeLabel(formatRelativeTimeLabel(timestamp));
}

function StatusLabel(props: { readonly threadId: string; readonly model: ArchivedRowActionModel }) {
  if (props.model.statusLabel === null) return null;
  return (
    <span
      data-testid={`thread-archive-state-${props.threadId}`}
      className={cn(
        "inline-flex justify-end text-[length:calc(var(--acard)*0.75)] leading-[var(--acard)] transition-opacity motion-reduce:transition-none",
        props.model.hasActions
          ? "group-hover/menu-sub-item:opacity-0 group-has-[:focus-visible]/sidebar-status-slot:opacity-0"
          : null,
        props.model.statusLabel === "Failed" ? "text-destructive" : "text-secondary-label",
      )}
    >
      {props.model.statusLabel}
    </span>
  );
}

/**
 * Reports the row age at rest and swaps in the available actions on hover or
 * keyboard focus. Both states occupy one fixed slot, so the title never moves.
 */
export function ActivityArchivedRowAgeSlot(props: {
  readonly rowProps: ActivityArchivedRowProps;
  readonly state: ActivityArchivedRowState;
}) {
  const { rowProps, state } = props;
  const model = resolveActionModel(rowProps, state);

  return (
    <span
      className={cn(
        "group/sidebar-status-slot relative ml-auto flex h-[calc(var(--acard)*1.5)] shrink-0 items-center justify-end",
        model.statusSlotWidthClass,
      )}
    >
      {model.statusLabel === null ? (
        <span
          data-testid={`thread-archived-age-${rowProps.thread.id}`}
          className="inline-flex justify-end text-[length:calc(var(--acard)*0.75)] leading-[var(--acard)] tabular-nums text-secondary-label transition-opacity motion-reduce:transition-none group-hover/menu-sub-item:opacity-0 group-has-[:focus-visible]/sidebar-status-slot:opacity-0"
        >
          {ageLabel(rowProps.thread)}
        </span>
      ) : (
        <StatusLabel threadId={rowProps.thread.id} model={model} />
      )}
      {model.hasActions ? (
        <span className="absolute inset-y-0 right-0 inline-flex items-center justify-end">
          <ArchiveAction
            rowProps={rowProps}
            state={state}
            archiveState={model.archiveState}
            archiveLabel={model.archiveLabel}
          />
          <RestoreAction rowProps={rowProps} state={state} model={model} />
        </span>
      ) : null}
    </span>
  );
}
